import type { ReactNode } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import type { Finding } from '@services/auditService';
import { severityChipColor, SEVERITY_FALLBACK } from '@utils/findingSeverity';
import { stripCodeSpans } from '@utils/codeSpans';
import { RuleTitle } from '@components/findings/RuleTitle';
import FailingCode from './FailingCode';

/**
 * REV-720: the one findings table.
 *
 * The org findings list and the scan-results table had grown into two
 * implementations of the same list — which is how they came to disagree about
 * what a finding is, what it is called, and whether it can be opened. They now
 * read the same endpoint (`/findings`, filtered by `scan_run_id` for a run) and
 * render through here.
 *
 * What a caller may vary is deliberately small:
 *
 *   - `columns` — which of the five to show. Not the ORDER: a caller picking
 *     a set is choosing what is relevant, while a caller picking an order would
 *     let two views of the same data disagree about their own shape.
 *   - `selection` — omit it and the checkbox column does not exist. This is how
 *     a view with no per-finding actions is expressed; there is no "disabled
 *     checkbox" state, because a checkbox that cannot be ticked is a control
 *     that exists only to be refused.
 *   - `hrefFor` — returning undefined for a row makes that row's rule id plain
 *     text rather than a dead link. Findings from an unregistered scan have no
 *     FindingInstance to open, so this is the normal case for them, not an
 *     error case.
 */
export type FindingColumn = 'rule' | 'severity' | 'source' | 'context' | 'target' | 'wcag';

/**
 * Canonical order. `columns` selects from this; it never reorders it.
 *
 * REV-508: a per-finding count column used to sit between `wcag` and the end.
 * It surfaced `occurrence_count`, which counts how many SCANS re-detected this
 * element — not how many places the rule fails. Every row here is already one
 * distinct issue (an instance is fingerprinted on rule + target + selector), so
 * the column answered a question nobody asks while implying it answered the one
 * everybody does. Removed rather than renamed; the distinct-occurrence count
 * belongs on finding detail (REV-539).
 */
export const FINDING_COLUMNS: readonly FindingColumn[] = [
  'rule',
  'severity',
  'source',
  'context',
  'target',
  'wcag',
];

export interface FindingsTableSelection {
  /** Selected rows, keyed by `raw_id`. */
  selected: ReadonlySet<string>;
  /** Whether this row may be selected at all (e.g. auto findings only). */
  isSelectable: (f: Finding) => boolean;
  /** How many rows on this page are selectable — drives the header checkbox. */
  selectableCount: number;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  onToggleOne: (rawId: string) => void;
}

/** Columns the table can sort by (REV-712). Mirrors the API's `sort` keys. */
export type FindingSortKey = 'severity' | 'rule';

export interface FindingsTableSort {
  by: FindingSortKey;
  direction: 'asc' | 'desc';
  /** The caller owns the order: a paged table must sort on the server. */
  onSort: (by: FindingSortKey) => void;
  /**
   * REV-715: which headers are sort buttons; default both. A table whose rows
   * all share one rule (a rule's instances) passes `['severity']`, because a
   * Rule header there would reorder nothing, and a button must do what its
   * name says.
   */
  sortable?: readonly FindingSortKey[];
}

export interface FindingsTableProps {
  rows: Finding[];
  loading?: boolean;
  /** Shown in the body when there is nothing to show and nothing is loading. */
  empty?: ReactNode;
  /** Where a row leads, or undefined when it leads nowhere. */
  hrefFor?: (f: Finding) => string | undefined;
  /**
   * A per-finding description to show under the rule name.
   *
   * A lookup rather than a field on the row: the engine's message lives in the
   * Mongo document an unregistered scan leaves behind, and `FindingInstance`
   * has no column for it, so a registered run has none to give. Declaring it on
   * the row type would promise the UI something the API never sends — the
   * REV-345 class of bug. The asymmetry stays visible at the call site instead.
   */
  descriptionFor?: (f: Finding) => string | undefined;
  selection?: FindingsTableSelection;
  /**
   * REV-712: omit it and the headers are plain text. Supplying it makes Rule
   * and Severity sort buttons; the table only reports the click, because a
   * paged caller has to re-query rather than sort the rows it happens to hold.
   */
  sort?: FindingsTableSort;
  columns?: readonly FindingColumn[];
  /** id of the heading that names this table. Prefer this over `ariaLabel`. */
  labelledBy?: string;
  ariaLabel?: string;
  /**
   * Name for the scrollable region around the table. Pass it whenever the table
   * has a meaningful name; the container is focusable (REV-507) and an unnamed
   * focusable element tells a screen-reader user nothing about what they have
   * landed in. Separate from `labelledBy` because that is an id, and the region
   * needs the text.
   */
  regionLabel?: string;
  /** Rendered under the table, inside the scroll container (e.g. pagination). */
  footer?: ReactNode;
  /** Renders the container as an outlined Paper. Off inside an existing card. */
  outlined?: boolean;
  size?: 'small' | 'medium';
}

/** Longest failing-code snippet a checkbox name may carry (REV-1146). */
const SNIPPET_MAX = 40;

/**
 * REV-1146: what identifies a finding's element in a control's name — its
 * selector, as the row shows it, or failing that a short, single-line slice of
 * its code. Never the whole code: a name is announced in full every time focus
 * lands on the control.
 */
const elementForName = (f: Pick<Finding, 'element_selector' | 'element_html'>) => {
  if (f.element_selector) return f.element_selector;
  const html = (f.element_html || '').replace(/\s+/g, ' ').trim();
  if (!html) return '';
  return html.length > SNIPPET_MAX ? `${html.slice(0, SNIPPET_MAX - 1)}…` : html;
};

/**
 * REV-1146: a finding's page for a control's name — the URL without its
 * scheme, which every row on a site shares and a listener would hear first.
 * The host stays: the org findings list spans tracked places, and two sites
 * can both have an /about.
 */
const pageForName = (url: string | null | undefined) =>
  (url || '').replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');

export default function FindingsTable({
  rows,
  loading = false,
  empty,
  hrefFor,
  descriptionFor,
  selection,
  sort,
  columns = FINDING_COLUMNS,
  labelledBy,
  ariaLabel,
  regionLabel,
  footer,
  outlined = false,
  size = 'medium',
}: FindingsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const shown = FINDING_COLUMNS.filter((c) => columns.includes(c));
  // REV-720: computed, not written down. The loading and empty rows used to
  // hardcode colSpan={6} against a table that had seven columns, so both sat
  // one column short of the full width for as long as they existed.
  const colSpan = shown.length + (selection ? 1 : 0);

  const heading: Record<FindingColumn, string> = {
    rule: t('findings.columns.rule', 'Rule'),
    severity: t('findings.columns.severity', 'Severity'),
    source: t('findings.columns.source', 'Source'),
    context: t('findings.columns.context', 'Tracked place'),
    target: t('findings.columns.target', 'Page'),
    wcag: t('findings.columns.wcag', 'WCAG'),
  };

  /**
   * REV-1146: the row checkbox's name. It used to be the rule alone, so a rule
   * failing on 33 elements gave 33 checkboxes one identical name and a
   * screen-reader user selecting rows for a bulk action could not tell them
   * apart. A finding is one rule on one element of one page (an instance is
   * fingerprinted on rule + target + selector), so the name carries all three.
   *
   * Computed rather than `aria-labelledby` at the row's cells: the selector and
   * page cells are optional (`columns`), and the rule cell also holds the
   * failing code, which does not belong in a control's name. Each part is read
   * from the same field the row displays, so the name says nothing the row
   * does not.
   */
  const selectOneLabel = (f: Finding) => {
    // REV-1163: a name has no markup, so a rule title's code spans lose their backticks.
    const name = stripCodeSpans(f.rule_name || f.rule_id || t('findings.untitled', 'finding'));
    const element = elementForName(f);
    const page = pageForName(f.target_url);
    if (element && page) {
      return t('findings.bulk.selectOneElementOnPage', {
        defaultValue: 'Select finding: {{name}}, {{element}}, on {{page}}',
        name,
        element,
        page,
      });
    }
    if (element) {
      return t('findings.bulk.selectOneElement', {
        defaultValue: 'Select finding: {{name}}, {{element}}',
        name,
        element,
      });
    }
    if (page) {
      return t('findings.bulk.selectOneOnPage', {
        defaultValue: 'Select finding: {{name}}, on {{page}}',
        name,
        page,
      });
    }
    return t('findings.bulk.selectOne', { defaultValue: 'Select finding: {{name}}', name });
  };

  /**
   * Every column except `rule`. The rule cell stays inline in the row below,
   * because it holds the row's real link and `convertedLinks.guard` can only
   * excuse a navigating row handler when it can SEE that link in the row's own
   * JSX. Moving it into a helper would not change what a user gets, but it
   * would put the pairing beyond the reach of the check that proves it.
   */
  const cell = (f: Finding, column: Exclude<FindingColumn, 'rule'>) => {
    switch (column) {
      case 'severity':
        return (
          <TableCell key={column}>
            <Chip
              size="small"
              color={severityChipColor(f.severity)}
              /* REV-720: the default used to be `f.severity`, the stored value,
                 and `findings.severities.*` did not exist — so every chip
                 rendered "critical" while the scan-results table, which this
                 table has to match, rendered "Critical". The keys exist now;
                 the fallback is a label rather than a database value, so a
                 missing one degrades to something a reader recognises. */
              label={t(`findings.severities.${f.severity}`, SEVERITY_FALLBACK[f.severity])}
            />
          </TableCell>
        );
      case 'source':
        return (
          <TableCell key={column}>
            {t(`findings.sources.${f.source}`, f.source === 'auto' ? 'Automated' : 'Manual')}
          </TableCell>
        );
      case 'context':
        return <TableCell key={column}>{f.context_name || '—'}</TableCell>;
      case 'target':
        // Plain text, not a link. A finding's page is context for reading the
        // row, and making it an anchor would put a second, competing
        // destination in a row that already navigates to the finding — the
        // reader would have two links and no way to tell which is the row's.
        return (
          <TableCell key={column} sx={{ wordBreak: 'break-all' }}>
            {f.target_url || '—'}
          </TableCell>
        );
      case 'wcag':
        return <TableCell key={column}>{(f.wcag_criteria || []).join(', ') || '—'}</TableCell>;
    }
  };

  return (
    <ScrollableTableContainer
      // REV-507 makes this container focusable so a keyboard user can scroll a
      // table wider than its box. A focusable element with no name announces
      // nothing useful, so name it when the caller has a name to give — and
      // only then: an unnamed region is worse than no region.
      {...(regionLabel ? { label: regionLabel } : {})}
      {...(outlined ? { component: Paper, variant: 'outlined' as const } : {})}
    >
      <Table
        size={size}
        {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      >
        <TableHead>
          <TableRow>
            {selection && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selection.allSelected}
                  indeterminate={selection.someSelected}
                  onChange={selection.onToggleAll}
                  disabled={selection.selectableCount === 0}
                  inputProps={{
                    'aria-label': t('findings.bulk.selectAll', 'Select all findings on this page'),
                  }}
                />
              </TableCell>
            )}
            {shown.map((c) => {
              const sortable =
                sort &&
                (c === 'rule' || c === 'severity') &&
                (sort.sortable ?? ['rule', 'severity']).includes(c);
              if (!sortable) return <TableCell key={c}>{heading[c]}</TableCell>;
              const active = sort.by === c;
              return (
                // sortDirection sets aria-sort on the active header only.
                <TableCell key={c} sortDirection={active ? sort.direction : false}>
                  <TableSortLabel
                    active={active}
                    direction={active ? sort.direction : 'asc'}
                    onClick={() => sort.onSort(c)}
                  >
                    {heading[c]}
                  </TableSortLabel>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={colSpan} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          )}
          {!loading && rows.length === 0 && empty && (
            <TableRow>
              <TableCell colSpan={colSpan} align="center">
                <Typography color="text.secondary">{empty}</Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            rows.map((f) => {
              const href = hrefFor?.(f);
              const selectable = selection?.isSelectable(f) ?? false;
              return (
                <TableRow
                  key={f.id}
                  // Nit, REV-720 review: a row that leads nowhere no longer
                  // lights up under the pointer. Hover feedback promising a
                  // click that does nothing is the unregistered-scan case this
                  // component is being built for.
                  hover={!!href}
                  sx={{ cursor: href ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (href) void navigate(href);
                  }}
                  // Both halves, as before: a row is shown selected only if
                  // it is selectable AND selected. Dropping the first half to
                  // shed a non-null assertion would have widened the condition
                  // — the ternary narrows `selection` without one.
                  selected={selection ? selectable && selection.selected.has(f.raw_id) : false}
                >
                  {selection && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      {selectable && (
                        <Checkbox
                          checked={selection.selected.has(f.raw_id)}
                          onChange={() => selection.onToggleOne(f.raw_id)}
                          inputProps={{ 'aria-label': selectOneLabel(f) }}
                        />
                      )}
                    </TableCell>
                  )}
                  {shown.map((c) =>
                    c === 'rule' ? (
                      <TableCell key={c}>
                        {href ? (
                          <Link
                            component={RouterLink}
                            to={href}
                            onClick={(e) => e.stopPropagation()}
                            variant="body2"
                            fontWeight={500}
                            underline="hover"
                          >
                            <RuleTitle text={f.rule_name || f.rule_id || '—'} />
                          </Link>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            <RuleTitle text={f.rule_name || f.rule_id || '—'} />
                          </Typography>
                        )}
                        {descriptionFor?.(f) && (
                          <Typography
                            component="div"
                            variant="body2"
                            color="text.secondary"
                            data-testid="finding-description"
                            sx={{ mt: 0.5 }}
                          >
                            {descriptionFor(f)}
                          </Typography>
                        )}
                        {f.element_selector && (
                          /* REV-720: the selector used to be an inline <span>
                             butted straight against the rule link, so the cell
                             read "Images must have alt texthtml > body > …" —
                             measured, not guessed. It is not only a visual
                             problem: that string is also what a screen reader
                             announces for the cell, as one unbroken phrase.
                             Its own block, with space above it. */
                          <Typography
                            component="div"
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-all' }}
                          >
                            {f.element_selector}
                          </Typography>
                        )}
                        {/* REV-713: the failing code itself, which is the more
                            useful of the two for verifying a finding. */}
                        {f.element_html && <FailingCode html={f.element_html} />}
                      </TableCell>
                    ) : (
                      cell(f, c)
                    )
                  )}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      {footer}
    </ScrollableTableContainer>
  );
}
